import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { SubSink } from 'subsink';

import {
  BaseScriptFragment,
  ListScriptsGQL,
  ListScriptsQueryVariables,
  RunScriptGQL,
} from '../../../generated/graphql.generated';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { MutateScriptComponent } from '../../shared/mutate-script/mutate-script.component';

@Component({
  selector: 'app-scripts',
  templateUrl: './scripts.component.html',
  styleUrls: ['./scripts.component.scss'],
})
export class ScriptsComponent implements OnInit, OnDestroy {

  @ViewChild('mutate') mutateRef: MutateScriptComponent;
  @ViewChild('scriptsTable') scriptsTable: Table;

  scripts: BaseScriptFragment[] = [];
  totalRecords = 0;
  loading = true;

  skip = 0;
  limit = 25;
  searchTerm = '';

  watchQuery: ReturnType<ListScriptsGQL['watch']>;

  private subs = new SubSink();

  constructor(
    private listScriptsGQL: ListScriptsGQL,
    private runScriptGQL: RunScriptGQL,
    private dhs: DetailsHelperService,
    private localNotify: FreyaNotificationsService,
  ) {}

  ngOnInit(): void {
    this.subs.sink = this.dhs.getObjectUpdates('Scripts').subscribe(() => {
      this.refetch();
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getVariables(): ListScriptsQueryVariables {
    return {
      filter: {
        search: this.searchTerm || undefined,
      },
      skip: this.skip,
      limit: this.limit,
      sort: 'name:ASC',
    };
  }

  loadScripts(ev?: LazyLoadEvent) {
    this.loading = true;
    if (ev) {
      this.skip = ev.first ?? 0;
      this.limit = ev.rows ?? 25;
    }

    if (!this.watchQuery) {
      this.watchQuery = this.listScriptsGQL.watch(this.getVariables(), {
        fetchPolicy: 'cache-and-network',
      });

      this.subs.sink = this.watchQuery.valueChanges.subscribe((res) => {
        if (!res.data) { return; }
        this.totalRecords = res.data.scripts?.total ?? 0;
        this.scripts = (res.data.scripts?.scripts as BaseScriptFragment[]) ?? [];
        this.loading = res.loading;
      });
    } else {
      // Refetch with the current variables (preserves skip/limit from the
      // lazy-load event). Do NOT call this.refetch() — it resets skip to 0.
      this.watchQuery.refetch(this.getVariables());
    }
  }

  refetch() {
    this.skip = 0;
    this.watchQuery?.refetch(this.getVariables());
  }

  onSearch() {
    this.refetch();
  }

  openCreate() {
    this.mutateRef.openDialog(undefined, 'create');
  }

  openEdit(script: BaseScriptFragment) {
    this.mutateRef.openDialog(script, 'update');
  }

  selectScript(script: BaseScriptFragment) {
    this.dhs.open('scripts', script);
  }

  runInstall(script: BaseScriptFragment) {
    this.subs.sink = this.runScriptGQL.mutate({
      scriptId: script.id,
      trigger: 'install',
    }).subscribe({
      next: () => {
        this.localNotify.success(`Install hook ran for "${script.name}". Check server logs for setup instructions.`);
      },
      error: (err) => {
        this.localNotify.apolloError(`Error running install hook`, err);
      },
    });
  }

  hasInstallHook(script: BaseScriptFragment): boolean {
    return script.hooks?.includes('install') ?? false;
  }
}
